
import { useState } from 'react';
import { utilityProviders } from '../../../mocks/relocation';

export default function UtilitiesSection() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <section id="utilities" className="py-20 px-6 bg-stone-50 scroll-mt-32">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 text-teal-700 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-plug-line"></i>
            Get Connected
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Utilities Setup Guide</h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm">
            Get your new home connected quickly. Roseville&apos;s city-owned utilities make setup simple with one-stop service for most essentials.
          </p>
        </div>

        <div className="space-y-4">
          {utilityProviders.map((utility, index) => (
            <div
              key={utility.category}
              className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-sm transition-shadow"
            >
              <button
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 cursor-pointer"
              >
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl"
                    style={{ backgroundColor: `${utility.color}15` }}
                  >
                    <i className={`${utility.icon} text-xl`} style={{ color: utility.color }}></i>
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-bold text-gray-900">{utility.category}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{utility.provider}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-400 hidden md:block">Setup: {utility.setupTime}</span>
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`ri-arrow-down-s-line text-gray-400 text-lg transition-transform ${expandedIndex === index ? 'rotate-180' : ''}`}></i>
                  </div>
                </div>
              </button>

              {expandedIndex === index && (
                <div className="px-5 pb-5 border-t border-gray-50 pt-4">
                  <div className="bg-amber-50 rounded-lg px-4 py-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <i className="ri-lightbulb-line mr-1.5"></i>
                      <strong>Tip:</strong> {utility.tip}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    {utility.phone && (
                      <a href={`tel:${utility.phone}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-teal-600 cursor-pointer">
                        <i className="ri-phone-line text-gray-400"></i>
                        {utility.phone}
                      </a>
                    )}
                    {utility.website && (
                      <a href={`https://${utility.website}`} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-2 text-sm text-teal-600 hover:text-teal-700 cursor-pointer">
                        <i className="ri-external-link-line text-gray-400"></i>
                        {utility.website}
                      </a>
                    )}
                    <span className="flex items-center gap-2 text-sm text-gray-500">
                      <i className="ri-time-line text-gray-400"></i>
                      {utility.setupTime}
                    </span>
                  </div>

                  {utility.options && (
                    <div className="grid md:grid-cols-3 gap-3">
                      {utility.options.map((opt) => (
                        <div key={opt.name} className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm font-semibold text-gray-900">{opt.name}</p>
                          <p className="text-xs text-gray-500 mt-1">{opt.speed}</p>
                          <p className="text-xs font-medium text-teal-600 mt-1">{opt.price}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Source Attribution */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Provider information sourced from City of Roseville Environmental Utilities, Roseville Electric, PG&E, AT&T, Comcast, and Consolidated Communications
          </p>
          <p className="text-[11px] text-gray-400 text-center mt-1">
            <i className="ri-time-line mr-1"></i>Data last updated: January 5, 2025
          </p>
        </div>
      </div>
    </section>
  );
}
